import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import { useCallback } from 'react';

import { useQueryClient } from '@eika-infrastruktur/react-query';

import { ensureAccountsData, useAccounts } from '../../../../fetchHooks/useAccount';
import type { Account } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { isValidAccountPaymentFrom } from '../../../../services/accountFilters';
import TranslationsConfigType from '../../../../translations/translationType';
import { doesAmountExceedAccountBalanceAccountBalance } from '../../../../utils/validation/amount';
import { isNotToday } from '../../../../utils/validation/date';
import { UseValidateHook } from '../../../../utils/validation/validateTypes';
import { DownpaymentState } from './state';

export interface DownpaymentError {
    accountId?: string;
    toAccount?: string;
    amount?: string;
    amountLimit?: string;
    note?: string;
}

export const isNoCoverage = (state: DownpaymentState, targetFromAccount: Account | undefined): boolean => {
    const { fromAccount, amount, paymentDate, firstBankingDateFromToday } = state;
    const isBankDaysBetweenNowAndPaymentDate =
        (firstBankingDateFromToday && paymentDate && isBefore(firstBankingDateFromToday, paymentDate)) ||
        isEqual(paymentDate!, firstBankingDateFromToday!);

    if (!fromAccount || !targetFromAccount) return false;
    if (!amount) return false;
    if (isBankDaysBetweenNowAndPaymentDate && isNotToday(paymentDate)) return false;

    return doesAmountExceedAccountBalanceAccountBalance(targetFromAccount, amount);
};

export const validateNote = (translated: TranslationsConfigType, note: string): string | undefined => {
    if (note && note.length && !note.replace(/\s/g, '').length) {
        return translated.invalidMessage;
    }

    return undefined;
};

export const validate =
    (translated: TranslationsConfigType, accounts: Account[]) =>
    (formState: DownpaymentState): DownpaymentError => {
        const errors: DownpaymentError = {
            accountId: undefined,
            amount: undefined,
            amountLimit: undefined,
            note: undefined,
            toAccount: undefined,
        };

        const { fromAccount, toAccount, amount, note } = formState;

        const targetFromAccount = accounts
            .filter(isValidAccountPaymentFrom)
            .find((account) => account.accountNumber.value === fromAccount?.value);
        const targetToAccount = accounts.find((account) => account.accountNumber.value === fromAccount?.value);

        if (!fromAccount || !targetFromAccount?.accountId || targetFromAccount?.accountId.value === '') {
            errors.accountId = translated.paymentValidation.chooseFromAccount;
        }

        if (!toAccount || !targetToAccount?.accountNumber || targetToAccount?.accountNumber.value === '') {
            errors.toAccount = translated.paymentValidation.chooseToAccount;
        }

        if (!amount || amount <= 0) {
            errors.amount = translated.paymentValidation.chooseAmount;
        } else if (isNoCoverage(formState, targetFromAccount)) {
            errors.amount = translated.paymentValidation.noCoverage;
        }

        errors.note = validateNote(translated, note);
        return errors;
    };

const useValidate: UseValidateHook<DownpaymentState, DownpaymentError> = () => {
    const translated = useTranslation();
    const queryClient = useQueryClient();
    const { data: accounts } = useAccounts();
    return useCallback(
        async (state) => {
            const currentAccounts = await ensureAccountsData(queryClient);
            return validate(translated, accounts ?? currentAccounts)(state);
        },
        [translated, queryClient, accounts],
    );
};
export default useValidate;
